import type React from 'react'
import { useRef, useState } from 'react'
import { getBottomBoundary } from '@/lib/editor/floating-canvas-chrome-bounds'
import { COLOR_LIST_DEFAULT_HEIGHT, clampListHeight } from '../helpers/clamp-list-height'
import { DRAGGABLE_POPOVER_ATTR } from './use-popover-drag'

/** Space kept between the popover's bottom edge and the bottom of the live area. */
const BOTTOM_MARGIN = 8

/**
 * Bottom-grip resize for the color list (All colors and search results), the same pointer-capture
 * drag as the Photo Effects list. Pass `listHeight` / `onListHeightChange` to keep the height in the
 * host, so it survives switching between Text, Fill and Stroke; otherwise it is local state.
 */
export function useListResize({
  listHeight: controlledHeight,
  onListHeightChange,
}: {
  listHeight?: number
  onListHeightChange?: (height: number) => void
} = {}) {
  const listRef = useRef<HTMLDivElement>(null)
  const [localHeight, setLocalHeight] = useState(COLOR_LIST_DEFAULT_HEIGHT)
  const listHeight = controlledHeight ?? localHeight
  const setListHeight = onListHeightChange ?? setLocalHeight
  const [isResizing, setIsResizing] = useState(false)
  const resizeRef = useRef<{
    pointerY: number
    startHeight: number
    contentHeight: number
    roomBelow: number
  } | null>(null)

  const handleResizeStart = (e: React.PointerEvent<HTMLButtonElement>) => {
    const list = listRef.current

    if (!list) return
    e.preventDefault()
    e.stopPropagation()

    const popover = e.currentTarget.closest<HTMLElement>(`[${DRAGGABLE_POPOVER_ATTR}]`)
    const popoverBottom =
      popover?.getBoundingClientRect().bottom ?? list.getBoundingClientRect().bottom

    resizeRef.current = {
      pointerY: e.clientY,
      startHeight: list.clientHeight,
      contentHeight: list.scrollHeight,
      roomBelow: Math.max(0, getBottomBoundary() - BOTTOM_MARGIN - popoverBottom),
    }
    setIsResizing(true)
    e.currentTarget.setPointerCapture(e.pointerId)
  }

  const handleResizeMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const resize = resizeRef.current

    if (!resize) return
    e.preventDefault()

    setListHeight(
      clampListHeight(resize.startHeight + e.clientY - resize.pointerY, resize.startHeight, resize)
    )
  }

  const handleResizeEnd = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!resizeRef.current) return
    resizeRef.current = null
    setIsResizing(false)

    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId)
    }
  }

  return { listRef, listHeight, isResizing, handleResizeStart, handleResizeMove, handleResizeEnd }
}
